import { Card } from "react-bootstrap";
import "../Events/Events.css";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import WebService from "../../utility/WebService";
import HelperService from "../../utility/HelperService";
import { useEffect, useState } from "react";
import { stat } from "fs";
import { eventNames } from "process";
import ViewTransactionDetails from "./ViewTransactionDetails";
import { EyeFill } from "react-bootstrap-icons";

type TabType = "EVENT" | "PLAN" | "RESOURCE";

const Transactions = () => {
  const CACHE_KEY = "transactions_state";
  const cachedState = sessionStorage.getItem(CACHE_KEY);
  const parsedCache = cachedState ? JSON.parse(cachedState) : null;
  const [activeTab, setActiveTab] = useState<TabType>("EVENT");
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState("createdAtFormatted");

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [showDetails, setShowDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  const sortFieldMap: Record<string, string> = {
    createdAtFormatted: "createdAt",
    // Sort by the SAME value the "Transaction ID" column displays per tab.
    invoiceNumber: activeTab === "EVENT"
      ? "payment.gatewayId"
      : "paymentGatewayId",
    totalAmountFormatted: "totalAmount",
    // Sort by the actual status shown per tab.
    status: activeTab === "EVENT"
      ? "payment.status"
      : "status",
    // Sort by the item name column (event / plan / resource) shown per tab.
    itemName: activeTab === "EVENT"
      ? "event.title"
      : activeTab === "PLAN"
        ? "plan.planName"
        : "report.title",
    paymentMethod: activeTab === "EVENT"
      ? "payment.method"
      : "paymentMethod",

    orderNo: activeTab === "EVENT"
      ? "booking.orderNo"
      : activeTab === "PLAN"
        ? "subscription.orderNo"
        : "resource.orderNo",
  };

  useEffect(() => {
    const stateToCache = {
      activeTab,
      currentPage,
      search,
      sortField,
      sortOrder,
      uiSortField,
      statusFilter,
      fromDate,
      toDate,
    };

    sessionStorage.setItem(CACHE_KEY, JSON.stringify(stateToCache));
  }, [activeTab, currentPage, search, sortField, sortOrder, uiSortField, statusFilter,
    fromDate,
    toDate,]);

  useEffect(() => {
    getTransactions();
  }, [activeTab, currentPage, search, sortField, sortOrder, statusFilter, fromDate, toDate]);

  const openTransactionDetails = (row: any) => {
    setSelectedTransaction(row);
    setShowDetails(true);
  };

  const formatDateTime = (value: string) => {
    if (!value) return "-";
    const d = new Date(value);
    return `${HelperService.dateFormatter(d.toISOString())} ${d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const renderStatus = (status: string) => {
    return (
      <span className={`badge ${status === "PAID" ? "bg-success" : status === "FAILED" ? "bg-danger" : "bg-warning"}`}>
        {status}
      </span>
    );
  };

  const toNumber = (val: any) => {
    if (!val) return 0;

    if (typeof val === "number") return val;

    if (typeof val === "string") return Number(val);

    if (val?.$numberDecimal) return Number(val.$numberDecimal);

    return 0;
  };

  const getOrderNo = (item: any) => {
    if (activeTab === "EVENT") {
      return item.booking?.orderNo || "-";
    }

    if (activeTab === "PLAN") {
      return item.subscription?.orderNo || "-";
    }

    if (activeTab === "RESOURCE") {
      return item.resource?.orderNo || "-";
    }

    return "-";
  };

  const getTransactions = () => {
    const params = new URLSearchParams();

    params.append("tab", activeTab);
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);

    if (search.trim()) {
      params.append("keyword", search.trim());
    }
    if (statusFilter) params.append("status", statusFilter);
    if (fromDate) params.append("fromDate", fromDate);
    if (toDate) params.append("toDate", toDate);

    WebService.getAPI({
      action: `plan/transaction-list?${params.toString()}`,
    }).then((res: any) => {
      const perm = res?.permissions || {};
      setPagePermission({
        canView: perm.canView,
        canAdd: perm.canAdd,
        canEdit: perm.canEdit,
        canDelete: perm.canDelete,
      });
      const data = res?.data || [];
      const pagination = res?.pagination || {};

      const formatted = data.map((item: any) => ({
        ...item,
        // transactionId: item?.transactionId ?? "-",

        invoiceNumber: activeTab === "EVENT" ? (item.payment?.gatewayId ?? "-") : item.paymentGatewayId,

        orderNo: getOrderNo(item),

        paymentMethod: activeTab === "EVENT"
          ? item.payment?.method === "MANNUAL" || item.payment?.method === "bycheck"
            ? "Manual"
            : "Online"
          : item.paymentMethod === "Admin Payment" || item.paymentMethod === "manual" ? "Manual" : "Online",

        itemName:
          activeTab === "EVENT"
            ? item.event?.title || "-"
            : activeTab === "PLAN"
              ? item.plan?.planName || "-"
              : item.report?.title || "-",

        userName: item.user?.fullName || "-",


        totalAmountFormatted:
          activeTab === "EVENT"
            ? `₹${toNumber(item.totalAmount)} INR`
            : `₹${Number(item.totalAmount || item.amount || 0)}`,

        status: renderStatus(item.paymentStatus || item.payment?.status || item.status),

        createdAtFormatted: formatDateTime(item.createdAt),

        action: (
          <EyeFill
            size={18}
            className="text-primary cursor-pointer"
            title="View Details"
            onClick={() => openTransactionDetails(item)}
          />
        ),
      }));

      setRows(formatted);
      setTotalCount(pagination.total || formatted.length);
    });
  };

  const getPaymentMethod = (item: any) => {
    // EVENT transactions
    if (activeTab === "EVENT") {
      const method = item.payment?.method;
      return method == "MANNUAL" ? "Manual" : "Online";
    }

    // PLAN / RESOURCE transactions
    return item.paymentMethod === "manual" ? "Manual" : "Online";
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      params.append("tab", activeTab);
      params.append("page", "1");
      params.append("limit", "100000"); // 🔥 VERY LARGE LIMIT

      if (search.trim()) params.append("keyword", search);
      if (statusFilter) params.append("status", statusFilter);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);

      const res: any = await WebService.getAPI({
        action: `plan/transaction-list?${params.toString()}`,
      });

      const data = res?.data || [];
      if (!data.length) return;

      // 🔥 SAME FORMAT AS TABLE
      const exportRows = data.map((item: any) => ({
        "Transaction ID":
          activeTab === "EVENT"
            ? item.payment?.gatewayId ?? "-"
            : item.paymentGatewayId ?? "-",

        [activeTab === "EVENT"
          ? "Event"
          : activeTab === "PLAN"
            ? "Plan"
            : "Resource"]:
          activeTab === "EVENT"
            ? item.event?.title
            : activeTab === "PLAN"
              ? item.plan?.planName
              : item.report?.title,

        User:
          activeTab === "EVENT"
            ? item.user?.fullName || "-"
            : typeof item.userId === "object"
              ? item.userId?.name || item.userId?.email || "-"
              : "-",

        // Amount: item.totalAmount,
        Amount: toNumber(item.totalAmount),
        Status: item.payment?.status || item.status,
        "Date & Time": HelperService.dateFormatter(item.createdAt),
      }));

      // 🔥 CSV CREATE (same as EventBookings)
      const headers = Object.keys(exportRows[0]);
      const csvRows: string[] = [];

      csvRows.push(headers.join(","));

      exportRows.forEach((row: any) => {
        const values = headers.map((h) => `"${row[h] ?? ""}"`);
        csvRows.push(values.join(","));
      });

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `transactions_${activeTab.toLowerCase()}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  return (


    <div className="events-main">
      <ViewTransactionDetails
        show={showDetails}
        handleClose={() => setShowDetails(false)}
        data={selectedTransaction}
        tab={activeTab}
      />
      <Card className="event-card">
        {/* Header */}
        <div className="mb-3">
          <div className="page-title mb-3">Transactions</div>



          {/* Search */}
          <input
            type="text"
            placeholder="Search..."
            className="form-control"
            style={{ width: 250 }}
            value={search}
            onChange={(e) => {
              const val = e.target.value;
              setSearch(val);
              setCurrentPage(1);
              //   getTransactions(val);
            }}
          />

          <div className="d-flex gap-3 mt-3 flex-wrap">
            {/* Status Filter */}
            <select
              className="form-select"
              style={{ width: 180 }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="PAID">PAID</option>
              <option value="FAILED">FAILED</option>
              <option value="PENDING">PENDING</option>
            </select>

            {/* From Date */}
            <input
              type="date"
              className="form-control"
              style={{ width: 180 }}
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
              }}
            />

            {/* To Date */}
            <input
              type="date"
              className="form-control"
              style={{ width: 180 }}
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Tabs */}
          <div className="d-flex gap-2 mt-3 align-items-center">
            {["EVENT", "PLAN", "RESOURCE"].map((tab) => (
              <button
                key={tab}
                className={`btn ${activeTab === tab ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => {
                  setActiveTab(tab as TabType);
                  setCurrentPage(1);
                }}>
                {tab}
              </button>
            ))}

            {pagePermission.canView && (
            <div style={{ marginLeft: "auto" }}>
              <button
                onClick={handleExport}
                className="btn"
                style={{
                  padding: "6px 14px",
                  background: "#4d0aebff",
                  color: "#fff",
                  borderRadius: 6,
                  border: "none",
                }}
              >
                Export
              </button>
            </div>
            )}
          </div>


        </div>

        {/* Table */}
        <CustomTableSortable
          columns={[
            //  {
            //   id: "transactionId",
            //   label: "ID",
            //   minWidth: 160,
            //   sortable: true,
            // },
            {
              id: "invoiceNumber",
              label: "Transaction ID",
              minWidth: 160,
              sortable: true,
            },
            {
              id: "orderNo",
              label: activeTab === "EVENT"
                  ? "Booking Id"
                  : activeTab === "PLAN"
                    ? "Subscription Id"
                    : "Report Id",
              minWidth: 160,
              sortable: true,
            },
            {
              id: "paymentMethod",
              label: "Payment Method",
              minWidth: 140,
              align: "center",
              sortable: true,
            },
            {
              id: "itemName",
              label:
                activeTab === "EVENT"
                  ? "Event"
                  : activeTab === "PLAN"
                    ? "Plan"
                    : "Resource",
              minWidth: 200,
              sortable: true,
            },
            {
              id: "userName",
              label: "User",
              minWidth: 200,
              sortable: false,
            },
            {
              id: "totalAmountFormatted",
              label: "Amount",
              minWidth: 140,
              align: "center",
              sortable: true,
            },
            {
              id: "status",
              label: "Status",
              minWidth: 120,
              align: "center",
              sortable: true,
            },
            {
              id: "createdAtFormatted",
              label: "Date & Time",
              minWidth: 180,
              sortable: true,
            },
            {
              id: "action",
              label: "Action",
              minWidth: 80,
              align: "center",
              sortable: false,
            }
          ]}
          rows={rows}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          sortField={uiSortField}
          sortOrder={sortOrder}
          onSortChange={(columnId, order) => {
            setUiSortField(columnId);
            setSortOrder(order);
            setSortField(sortFieldMap[columnId] || "createdAt");
            setCurrentPage(1);
            // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
          }}
        />
      </Card>
    </div>
  );
};

export default Transactions;
